import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppState, useFunctionPolling } from '@garnish/core';
import { useClient } from 'urql';

import { OrderState } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsBagOpen } from '../useBagNavigation/useBagNavigation';
import { CartDocument, type CartQuery } from '../useCart/GraphQL/Cart.generated';
import { useCart } from '../useCart/useCart';
import {
  GroupOrderPostOrderDocument,
  type GroupOrderPostOrderQuery,
} from './GraphQL/GroupOrderPostOrder.generated';
import {
  checkIfActiveGroupOrderHost,
  checkIfGroupOrderDismissed,
  getActiveGroupOrderId,
  storeActiveGroupOrder,
  storeDismissedGroupOrderId,
} from './group-order-reference-storage';
import { getTerminalGroupOrder } from './group-order-terminal-state';
import { useResetGroupOrderingState } from './useResetGroupOrderingState';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Synchronizes active group-order state by polling while the app is active and
 * outside Bag, then clears stale frontend state when the order ends or disappears.
 */
export const useGroupOrderingState = () => {
  const client = useClient();
  const { cart, hasLoadedCart } = useCart();
  const isBagOpen = useIsBagOpen();

  const resetGroupOrderingState = useResetGroupOrderingState();

  const pollingIntervalSeconds = useFeatureFlag('CELS-5826-order-group-order-polling-interval-seconds'); // prettier-ignore

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const checkedTerminalGroupOrderIdRef = useRef<string | undefined>(undefined);

  // The reset function changes identity on navigation; reading it through a ref
  // keeps screen changes out of the terminal check's dependency chain.
  const resetGroupOrderingStateRef = useRef(resetGroupOrderingState);

  resetGroupOrderingStateRef.current = resetGroupOrderingState;

  // ─── State ────────────────────────────────────────────────────────────────

  const [terminalGroupOrder, setTerminalGroupOrder] =
    useState<ReturnType<typeof getTerminalGroupOrder>>();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const pollingInterval = pollingIntervalSeconds * MILLISECONDS_PER_SECOND;

  const isGroupOrder = Boolean(cart?.multipartyDetails);
  const isHost = Boolean(cart?.currentUserOrderPermissions.canSubmitOrder);
  const isGroupOrderFinished = checkIfGroupOrderFinished(cart);
  const isCurrentGroupOrderActive = isGroupOrder && !isGroupOrderFinished;
  const currentGroupOrderId = isGroupOrder ? cart?.id : undefined;
  const storedActiveGroupOrderId = getActiveGroupOrderId();
  const groupOrderId = currentGroupOrderId ?? storedActiveGroupOrderId;

  const shouldCheckTerminalGroupOrder = Boolean(
    hasLoadedCart && groupOrderId && !isCurrentGroupOrderActive,
  );
  const isPollingEnabled = isCurrentGroupOrderActive && !isBagOpen && pollingIntervalSeconds > 0; // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTerminalGroupOrder = useCallback(
    async (orderId: string) => {
      const isHostOrder = checkIfActiveGroupOrderHost(orderId);

      if (isHostOrder) {
        resetGroupOrderingStateRef.current();
        storeDismissedGroupOrderId(orderId);

        return;
      }

      const response = await client
        .query<GroupOrderPostOrderQuery>(
          GroupOrderPostOrderDocument,
          { orderId },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      const order = response.data?.order;
      const isNetworkError = Boolean(response.error?.networkError);

      // A transport failure says nothing about the order, so leave the reference
      // in place and allow the next trigger to retry instead of forgetting it.
      if (!order && isNetworkError) {
        checkedTerminalGroupOrderIdRef.current = undefined;

        return;
      }

      // A stored reference to an order the backend can no longer return must be
      // forgotten without touching the customer's current location; resetting
      // here would keep undoing every location change until the reference expires.
      if (!order) {
        storeDismissedGroupOrderId(orderId);

        return;
      }

      resetGroupOrderingStateRef.current();

      const terminalOrder = getTerminalGroupOrder(order);
      const hasDismissedOrder = terminalOrder
        ? checkIfGroupOrderDismissed(terminalOrder.id)
        : false;

      if (!terminalOrder || hasDismissedOrder) return;

      setTerminalGroupOrder(terminalOrder);
    },
    [client],
  );

  const handlePoll = useCallback(async () => {
    if (!isPollingEnabled || !currentGroupOrderId) return;

    await client.query<CartQuery>(CartDocument, {}, { requestPolicy: 'network-only' }).toPromise();
  }, [client, currentGroupOrderId, isPollingEnabled]);

  const clearTerminalGroupOrder = useCallback(() => {
    setTerminalGroupOrder(undefined);
  }, []);

  const handleActiveGroupOrderChange = useCallback(() => {
    if (!currentGroupOrderId || !isCurrentGroupOrderActive) return;

    // The order is live again, so a later terminal transition must be checked
    // even if the same id was already checked while it was not in the cart.
    checkedTerminalGroupOrderIdRef.current = undefined;

    storeActiveGroupOrder({ orderId: currentGroupOrderId, isHost });
  }, [currentGroupOrderId, isCurrentGroupOrderActive, isHost]);

  // ─── App State ─────────────────────────────────────────────────────────────

  const appState = useAppState({
    pause: !isPollingEnabled && !shouldCheckTerminalGroupOrder,
    onVisible: handlePoll,
  });

  const shouldSkipPolling = !isPollingEnabled || appState !== 'active';

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTerminalGroupOrderChange = useCallback(() => {
    if (!shouldCheckTerminalGroupOrder || !groupOrderId || appState !== 'active') return;

    const hasCheckedGroupOrder = checkedTerminalGroupOrderIdRef.current === groupOrderId;

    if (hasCheckedGroupOrder) return;

    checkedTerminalGroupOrderIdRef.current = groupOrderId;

    void handleTerminalGroupOrder(groupOrderId);
  }, [appState, groupOrderId, handleTerminalGroupOrder, shouldCheckTerminalGroupOrder]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(handleActiveGroupOrderChange, [handleActiveGroupOrderChange]);
  useEffect(handleTerminalGroupOrderChange, [handleTerminalGroupOrderChange]);

  useFunctionPolling(pollingInterval, handlePoll, shouldSkipPolling);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    terminalGroupOrder,
    clearTerminalGroupOrder,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfGroupOrderFinished(cart: CartQuery['cart'] | undefined) {
  return (
    !cart || cart.orderState === OrderState.Complete || cart.orderState === OrderState.Discarded
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MILLISECONDS_PER_SECOND = 1000;
